import type { SubmitAccessibility } from "common/apis";
import type { HearAboutUsGroup, HearAboutUsOption } from "common/types";

type ReferralSubmit = Pick<
  SubmitAccessibility,
  "how_do_you_hear_about_us_ids" | "how_do_you_hear_about_us_group_ids"
>;

const splitIds = (csv: string | undefined) =>
  (csv || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

/**
 * The Mixpanel `referral_source` value for a sign-up: group names when the
 * wizard submitted group ids, else option titles. Group and option ids are
 * separate sequences that overlap, so each id space gets its own lookup.
 * @returns comma-joined names; an unknown id is kept as-is; "" when nothing was picked.
 */
export function referralSourceLabel(
  submitData: ReferralSubmit,
  options: readonly HearAboutUsOption[],
  groups: readonly HearAboutUsGroup[],
): string {
  const groupIds = splitIds(submitData.how_do_you_hear_about_us_group_ids);
  if (groupIds.length > 0) {
    const groupNameById = new Map(groups.map((g) => [String(g.id), g.name]));
    return groupIds.map((id) => groupNameById.get(id) ?? id).join(", ");
  }

  const optionTitleById = new Map(options.map((o) => [String(o.id), o.title]));
  return splitIds(submitData.how_do_you_hear_about_us_ids)
    .map((id) => optionTitleById.get(id) ?? id)
    .join(", ");
}
